import { isDefined } from '@sgme/fp';
import { Tooltip } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { Form } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import {
  useCheckTargetAccumulatorByTradeCapture,
  useTargetAccumulatorSelector,
  useTargetAccumulatorTileId,
} from '@/App/tiles/TargetAccumulator/TargetAccumulatorContext.tsx';
import { TargetAccumulatorFormField } from '@/App/tiles/TargetAccumulator/TargetAccumulatorFormField.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import {
  selectTargetAccumulatorFixingReference1Data,
  selectTargetAccumulatorisCrossedData,
} from '@/store/targetAccumulator/selectors.ts';

const FixingReference1 = () => {
  const tileId = useTargetAccumulatorTileId();
  const { fixingReference1, fixingReference1List, fixingReference1Default } = useAppSelector((state) =>
    selectTargetAccumulatorFixingReference1Data(state, tileId),
  );
  const { crossCurrency } = useAppSelector((state) => selectTargetAccumulatorisCrossedData(state, tileId));
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const currencyPair = useTargetAccumulatorSelector((targetAccumulator) => targetAccumulator?.properties.currencyPair);
  const [currency1, currency2] = currencyPair ? currencyPair.split('/') : [undefined, undefined];
  const isSameReferenceAsDefault = fixingReference1 === fixingReference1Default;

  const currencyPairDisplayed =
    isDefined(crossCurrency) && crossCurrency !== '' && crossCurrency !== currency2 && crossCurrency !== currency1
      ? `${currency1}/${crossCurrency}`
      : currencyPair;

  const checkTargetAccumulatorWithTradeCapture = useCheckTargetAccumulatorByTradeCapture();

  const handleFixingReference1 = (event: React.ChangeEvent<HTMLSelectElement>) => {
    sgwtWebAnalytics?.trackEvent(
      'Target Accumulator',
      'Fixing reference 1 changed',
      isDefined(event.target.value) ? event.target.value : 'cleared',
    );

    return checkTargetAccumulatorWithTradeCapture({
      fixingReference1: event.target.value,
    });
  };

  return (
    <TargetAccumulatorFormField
      formattedMessageId="targetAccumulator.form.fixingReference"
      formattedMessageValues={{ currencyPair: currencyPairDisplayed ?? '' }}
      extra={
        !isSameReferenceAsDefault ? (
          <Tooltip
            position="right"
            contents={
              <FormattedMessage
                id="targetAccumulator.form.fixingReference.info"
                values={{ defaultFixingReference: fixingReference1Default, br: <br /> }}
              />
            }
          >
            <div className="badge badge-dismissible">
              <em className="icon icon-sm cursor-default line-height-1 text-secondary">info_outline</em>
            </div>
          </Tooltip>
        ) : undefined
      }
    >
      <Form.Select
        value={fixingReference1 ?? fixingReference1Default}
        onChange={handleFixingReference1}
        className={`${!isSameReferenceAsDefault ? 'text-warning fw-bold' : ''}`}
        size="sm"
        data-e2e="fixing-reference1-selector"
      >
        {fixingReference1List?.map((ref) => (
          <option className="text-primary" key={ref} value={ref}>
            {ref}
          </option>
        ))}
      </Form.Select>
    </TargetAccumulatorFormField>
  );
};

export default FixingReference1;
